import { useRef } from 'react';
import { useToken } from '@chakra-ui/system';

import { useFourthlineSdkScript } from './hooks';
import { FourthlineCssVariable } from './types';
import { useFourthlineSdkHandlers } from './useFourthlineSdkHandlers';

interface FourthlineSdkProps {
  token: string;
  onFlowCompleted: () => void;
  onFlowError?: () => void;
  onRestartFlow: () => void;
  successRedirectUrl: string;
  failureRedirectUrl: string;
  trackingEvents: {
    sdkLoaded: string;
  };
}

const FourthlineSdk = ({
  failureRedirectUrl,
  onFlowCompleted,
  onFlowError,
  onRestartFlow,
  successRedirectUrl,
  token,
  trackingEvents,
}: FourthlineSdkProps) => {
  const containerRef = useRef<HTMLElement>(null);

  useFourthlineSdkScript();

  useFourthlineSdkHandlers({
    containerRef,
    failureRedirectUrl,
    onFlowCompleted,
    onFlowError,
    onRestartFlow,
    successRedirectUrl,
    trackingEvents,
  });

  const customStyles = {
    '--fl-button-border-radius': '9999px',

    '--fl-button-primary-background-color': useToken('colors', 'grey.800'),
    '--fl-button-primary-border-color': 'transparent',
    '--fl-button-primary-color': useToken('colors', 'white'),

    '--fl-color-accent': useToken('colors', 'yellow.500'),
    '--fl-color-dark': useToken('colors', 'text-primary'),
    '--fl-color-dark-light': useToken('colors', 'text-secondary'),
    '--fl-color-dark-lighter': useToken('colors', 'text-light'),
    '--fl-color-info': useToken('colors', 'blue.600'),
    '--fl-color-primary': useToken('colors', 'grey.800'),
    '--fl-color-warning': useToken('colors', 'orange.500'),

    '--fl-font-family': useToken('fonts', 'body'),

    '--fl-link-font-weight': useToken('fontWeights', 'weight-500'),

    // This is the padding of the main container - we already have spacings on our own.
    '--fl-main-spacing': '0px',
  } satisfies Partial<Record<FourthlineCssVariable, string>>;

  return (
    <fl-flow-onboarding ref={containerRef} style={customStyles} token={token} />
  );
};

export default FourthlineSdk;
